import type { ComponentPublicInstance, MaybeRefOrGetter } from "vue";
import { computed, nextTick, toValue } from "vue";

export interface RadioGroupOption {
  checked: boolean
  disabled: boolean
}

const NEXT_KEYS = new Set(["ArrowRight", "ArrowDown"]);
const PREVIOUS_KEYS = new Set(["ArrowLeft", "ArrowUp"]);

/**
 * Keyboard behaviour of a custom radio group: the group is one tab stop (the checked radio, otherwise the
 * first enabled one), and the arrow keys, Home and End move focus to another enabled radio and check it.
 */
export function useRadioGroup(
  options: MaybeRefOrGetter<RadioGroupOption[]>,
  check: (index: number) => void,
) {
  const elements = new Map<number, HTMLElement>();

  const tabStop = computed(() => {
    const list = toValue(options);
    const checked = list.findIndex(option => option.checked && !option.disabled);
    if (checked >= 0) {
      return checked;
    }
    return list.findIndex(option => !option.disabled);
  });

  function setRadioRef(element: Element | ComponentPublicInstance | null, index: number) {
    if (element instanceof HTMLElement) {
      elements.set(index, element);
      return;
    }
    elements.delete(index);
  }

  function radioTabIndex(index: number): 0 | -1 {
    return index === tabStop.value ? 0 : -1;
  }

  function enabledIndexes(): number[] {
    return toValue(options).flatMap((option, index) => (option.disabled ? [] : [index]));
  }

  function targetIndex(key: string, current: number): number | null {
    const enabled = enabledIndexes();
    if (enabled.length === 0) {
      return null;
    }
    if (key === "Home") {
      return enabled[0] ?? null;
    }
    if (key === "End") {
      return enabled[enabled.length - 1] ?? null;
    }
    const position = enabled.indexOf(current);
    if (NEXT_KEYS.has(key)) {
      return enabled[(position + 1) % enabled.length] ?? null;
    }
    if (PREVIOUS_KEYS.has(key)) {
      return enabled[(position - 1 + enabled.length) % enabled.length] ?? null;
    }
    return null;
  }

  function onRadioKeydown(event: KeyboardEvent, index: number) {
    const target = targetIndex(event.key, index);
    if (target === null) {
      return;
    }
    event.preventDefault();
    check(target);
    nextTick(() => elements.get(target)?.focus());
  }

  return { setRadioRef, radioTabIndex, onRadioKeydown };
}
